/**
 * Mode-Aware API Client for Storybook API
 * 
 * Supports three operational modes:
 * - Development Mode: X-User-Id header (no auth required)
 * - Debug Mode: JWT tokens with Cognito test users
 * - Production Mode: JWT tokens with real OAuth
 */

import { fetchAuthSession } from 'aws-amplify/auth';

const DEBUG = process.env.NODE_ENV !== 'production';

const log = (...args) => {
  if (DEBUG) console.log('[API CLIENT]', ...args);
};

// Global error handler for showing toast notifications
let globalErrorHandler = null;

export const setApiErrorHandler = (handler) => {
  globalErrorHandler = handler;
  log('Global API error handler registered');
};

/**
 * Mode detection based on environment variables
 * Made dynamic to support testing
 */
const MODE = {
  get isDevelopment() {
    return process.env.REACT_APP_DEV_MODE === 'true';
  },
  get isDebug() {
    return process.env.REACT_APP_DEBUG_MODE === 'true';
  },
  get isProduction() {
    return !this.isDevelopment && !this.isDebug;
  },
};

class ApiClient {
  constructor(baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001') {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };

    // Initialize userId from localStorage or default
    this.userId = localStorage.getItem('user_id') || 'dev-user-123';

    log('API Client initialized:', {
      baseURL: this.baseURL,
      userId: this.userId
    });
  }

  /**
   * Get current operational mode
   */
  getMode() {
    if (MODE.isDevelopment) return 'development';
    if (MODE.isDebug) return 'debug';
    return 'production';
  }

  /**
   * Check if authentication is required
   */
  requiresAuth() {
    return !MODE.isDevelopment;
  }

  /**
   * Set user ID for development mode
   */
  setUserId(userId) {
    this.userId = userId;
    localStorage.setItem('user_id', userId);
    log('User ID set to:', userId);
  }

  /**
   * Get authentication headers
   */
  async getAuthHeaders() {
    const headers = {};

    if (MODE.isDevelopment) {
      headers['X-User-Id'] = this.userId;
      log('Using dev auth header:', headers);
      return headers;
    }

    // Production/Debug mode — send JWT Bearer token
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      log('Failed to get auth session:', error.message);
    }

    log('Using auth headers:', Object.keys(headers));
    return headers;
  }

  /**
   * Refresh authentication token
   */
  async refreshToken() {
    if (MODE.isDevelopment) return;
    await fetchAuthSession({ forceRefresh: true });
  }

  /**
   * Set base URL for all requests
   */
  setBaseURL(url) {
    this.baseURL = url;
    log('Base URL updated to:', url);
  }

  /**
   * Set default headers for all requests
   */
  setDefaultHeaders(headers) {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
    log('Default headers updated:', this.defaultHeaders);
  }

  /**
   * Generic request method with automatic auth and retry
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    try {
      // Get auth headers
      const authHeaders = await this.getAuthHeaders();

      const config = {
        headers: {
          ...this.defaultHeaders,
          ...authHeaders,
          ...options.headers
        },
        ...options,
      };

      log(`${config.method || 'GET'} ${url}`);
      if (config.body && DEBUG) {
        try {
          const bodyData = JSON.parse(config.body);
          log('Request body:', bodyData);
        } catch (e) {
          log('Request body (non-JSON):', config.body?.substring(0, 100) + '...');
        }
      }

      const response = await fetch(url, config);

      log(`Response: ${response.status} ${response.statusText}`);

      // Handle 401 Unauthorized - token expired
      if (response.status === 401 && !MODE.isDevelopment) {
        log('Received 401, attempting token refresh...');

        try {
          await this.refreshToken();

          // Retry request with new token
          const newAuthHeaders = await this.getAuthHeaders();
          config.headers = {
            ...config.headers,
            ...newAuthHeaders,
          };

          log('Retrying request with refreshed token...');
          return await this.request(endpoint, options);
        } catch (refreshError) {
          log('Token refresh failed, redirecting to login');
          this.handleAuthenticationFailure();
          throw new Error('Session expired. Please log in again.');
        }
      }

      // Handle 403 Forbidden - insufficient permissions
      if (response.status === 403) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }

        const error = new Error(errorData.message || 'Access denied');
        error.status = 403;
        error.code = 'FORBIDDEN';
        error.data = errorData;
        throw error;
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${errorText}`;
        let errorData = null;

        // Try to parse error response as JSON
        try {
          errorData = JSON.parse(errorText);

          // Handle validation errors with detailed field information
          if (errorData.error_code === 'BAD_REQUEST' && errorData.message) {
            errorMessage = errorData.message;

            // Try to extract field-specific validation errors for better UX
            const validationMatch = errorData.message.match(/\[({.*})\]/);
            if (validationMatch) {
              try {
                const validationError = JSON.parse(validationMatch[1].replace(/'/g, '"'));
                errorMessage = `Validation error: ${validationError.field} - ${validationError.message}`;
              } catch (e) {
                // Keep the original message if parsing fails
              }
            }
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // Not JSON, use raw text
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.response = response;
        error.data = errorData;
        throw error;
      }

      const contentType = response.headers.get('content-type');
      let data;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
        log('Response data:', data);
      } else {
        data = await response.text();
        log('Response text length:', data.length);
      }

      return {
        data,
        status: response.status,
        headers: response.headers,
        ok: response.ok,
      };

    } catch (error) {
      log('Request failed:', error.message);

      // Handle authentication errors
      if (error.code === 'AUTH_REQUIRED') {
        this.handleAuthenticationFailure();
      }

      // Enhance error with more context
      if (!error.status) {
        error.message = `Network error: ${error.message}`;
      }

      // Show error to user via global handler
      if (globalErrorHandler) {
        globalErrorHandler(error.message);
      }

      throw error;
    }
  }

  /**
   * Handle authentication failure (redirect to login)
   */
  handleAuthenticationFailure() {
    if (!MODE.isDevelopment) {
      // Store current location to redirect back after login
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      // Redirect to login page
      window.location.href = '/login';
    }
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    return this.request(url, {
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post(endpoint, data = null, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : null,
      ...options,
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data = null, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : null,
      ...options,
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  /**
   * Upload file with multipart/form-data
   */
  async upload(endpoint, formData, options = {}) {
    const uploadOptions = {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type for FormData, let browser set it with boundary
        ...Object.fromEntries(
          Object.entries(this.defaultHeaders).filter(([key]) =>
            key.toLowerCase() !== 'content-type'
          )
        ),
        ...options.headers,
      },
      ...options,
    };

    return this.request(endpoint, uploadOptions);
  }

  // ==================== Profile API Methods ====================

  /**
   * Get user profile
   */
  async getProfile() {
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();

    // 1. In-memory cache (same-session navigations)
    if (this._profileCache && (now - this._profileCacheTime) < CACHE_TTL) {
      return this._profileCache;
    }

    // 2. sessionStorage cache (survives page reloads within the same tab)
    try {
      const stored = sessionStorage.getItem('profile_cache');
      if (stored) {
        const { data, time } = JSON.parse(stored);
        if ((now - time) < CACHE_TTL) {
          this._profileCache = data;
          this._profileCacheTime = time;
          return data;
        }
      }
    } catch (e) { /* ignore parse errors */ }

    // 3. Fetch from API
    const response = await this.get('/profile');
    this._profileCache = response.data;
    this._profileCacheTime = now;
    try {
      sessionStorage.setItem('profile_cache', JSON.stringify({ data: response.data, time: now }));
    } catch (e) { /* ignore storage errors (e.g. private mode quota) */ }
    return response.data;
  }

  invalidateProfileCache() {
    this._profileCache = null;
    this._profileCacheTime = 0;
    try { sessionStorage.removeItem('profile_cache'); } catch (e) {}
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData) {
    this.invalidateProfileCache();
    const response = await this.put('/profile', profileData);
    return response.data;
  }

  /**
   * Get user statistics
   */
  async getProfileStats() {
    const response = await this.get('/profile/stats');
    return response.data;
  }

  /**
   * Add a child profile
   */
  async addChild(childData) {
    const response = await this.post('/profile/children', childData);
    return response.data;
  }

  /**
   * Update a child profile
   */
  async updateChild(childId, childData) {
    const response = await this.put(`/profile/children/${childId}`, childData);
    return response.data;
  }

  /**
   * Delete a child profile
   */
  async deleteChild(childId) {
    const response = await this.delete(`/profile/children/${childId}`);
    return response.data;
  }

  /**
   * Get user's saved character library
   */
  async getCharacters() {
    const response = await this.get('/profile/characters');
    return response.data;
  }

  /**
   * Delete a saved character from the library
   */
  async deleteCharacter(characterId) {
    const response = await this.delete(`/profile/characters/${characterId}`);
    return response.data;
  }

  /**
   * Update a saved character in the library
   */
  async updateCharacter(characterId, data) {
    const response = await this.put(`/profile/characters/${characterId}`, data);
    return response.data;
  }

  // ==================== Payment API Methods ====================

  /**
   * Get pricing plans
   */
  async getPricing() {
    const response = await this.get('/pricing');
    return response.data;
  }

  /**
   * Create Stripe checkout session
   */
  async createCheckoutSession(priceId, successUrl, cancelUrl) {
    const response = await this.post('/checkout', {
      price_id: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
    return response.data;
  }

  /**
   * Get Stripe billing portal URL
   */
  async getBillingPortal(returnUrl) {
    const response = await this.post('/billing/portal', {
      return_url: returnUrl,
    });
    return response.data;
  }

  /**
   * Get payment history
   */
  async getPaymentHistory() {
    const response = await this.get('/billing/history');
    return response.data;
  }

  /**
   * Get subscription status
   */
  async getSubscriptionStatus() {
    const response = await this.get('/billing/subscription');
    return response.data;
  }

  // ==================== Story API Methods ====================

  /**
   * Create a new story
   */
  async createStory(storyData) {
    const response = await this.post('/stories', storyData);
    return response.data;
  }

  /**
   * Get a specific story by ID
   */
  async getStory(storyId) {
    const response = await this.get(`/stories/${storyId}`);
    return response.data;
  }

  /**
   * List all stories for the user
   */
  async listStories(pageSize = 20, nextToken = null) {
    const params = { page_size: pageSize };
    if (nextToken) {
      params.next_token = nextToken;
    }
    const response = await this.get('/stories', params);
    return response.data;
  }

  /**
   * Check story status (for polling)
   */
  async checkStoryStatus(storyId) {
    const response = await this.get(`/stories/${storyId}/status`);
    return response.data;
  }

  // ==================== Utility Methods ====================

  /**
   * Polling utility for long-running operations
   */
  async poll(endpoint, options = {}) {
    const {
      maxAttempts = 60,
      interval = 5000,
      onProgress = null,
      successCondition = (data) => data.status === 'completed',
      failureCondition = (data) => data.status === 'failed',
    } = options;

    let attempts = 0;

    return new Promise((resolve, reject) => {
      const pollFn = async () => {
        try {
          const response = await this.get(endpoint);
          const data = response.data;

          if (onProgress) {
            onProgress(data, attempts, maxAttempts);
          }

          if (successCondition(data)) {
            log('Polling completed successfully');
            resolve(response);
            return;
          }

          if (failureCondition(data)) {
            const error = new Error('Operation failed');
            error.data = data;
            reject(error);
            return;
          }

          // Continue polling
          attempts++;
          if (attempts < maxAttempts) {
            log(`Polling attempt ${attempts}/${maxAttempts}, retrying in ${interval}ms`);
            setTimeout(pollFn, interval);
          } else {
            reject(new Error('Polling timeout exceeded'));
          }

        } catch (error) {
          log('Polling error:', error.message);

          // Retry on network errors
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollFn, interval);
          } else {
            reject(error);
          }
        }
      };

      pollFn();
    });
  }

  /**
   * Batch requests utility
   */
  async batch(requests) {
    log(`Executing ${requests.length} batch requests`);

    const promises = requests.map(async (req, index) => {
      try {
        const result = await this.request(req.endpoint, req.options);
        return { index, success: true, data: result.data };
      } catch (error) {
        return { index, success: false, error: error.message };
      }
    });

    const results = await Promise.all(promises);

    const successful = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);

    log(`Batch completed: ${successful.length} successful, ${failed.length} failed`);

    return {
      results,
      successful,
      failed,
      allSuccessful: failed.length === 0,
    };
  }

  /**
   * Retry utility for failed requests
   */
  async retry(requestFn, options = {}) {
    const { maxRetries = 3, delay = 1000, backoff = 2 } = options;

    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;

        if (attempt === maxRetries) {
          break;
        }

        const waitTime = delay * Math.pow(backoff, attempt);
        log(`Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${waitTime}ms`);

        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }

    throw lastError;
  }

  /**
   * Test API connectivity
   */
  async testConnection() {
    try {
      // Try to get profile - if this works, API is connected
      await this.getProfile();
      return {
        success: true,
        message: 'API connection successful',
        mode: this.getMode(),
      };
    } catch (error) {
      return {
        success: false,
        message: error.message,
        mode: this.getMode(),
        details: 'Make sure the backend API is running and properly configured.'
      };
    }
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
export { ApiClient, MODE };
